import {
  getOrdinalSuffix,
  ISavedFilterSchedule,
  policyWeeklyDayIntervalMap,
  SAVED_FILTER_SCHEDULE_MAX_RECIPIENTS,
  SavedFilterScheduleInterval,
  UserTypes,
} from 'shared';

import { Select } from '../../../common/Atoms/Select';
import { MultiSelect } from '../../../common/Atoms/MultiSelect';
import { useCurrentOrgUsers } from '../../hooks/useCurrentOrgUsers';
import { useUser } from '../../hooks/useUser';

interface SavedFilterScheduleFieldsProps {
  schedule: ISavedFilterSchedule | null;
  onChange: (schedule: ISavedFilterSchedule | null) => void;
  disabled?: boolean;
}

const NO_EMAIL = `none`;
const DEFAULT_HOUR = 7;

const intervalOptions = [
  { label: `No email`, value: NO_EMAIL },
  { label: `Weekly`, value: SavedFilterScheduleInterval.Weekly },
  { label: `Monthly`, value: SavedFilterScheduleInterval.Monthly },
];

const weekdayOptions = Object.entries(policyWeeklyDayIntervalMap).map(([value, label]) => ({
  label,
  value: Number(value),
}));

const monthDayOptions = Array.from(Array(31).keys()).map(i => ({
  label: `${i + 1}${getOrdinalSuffix(i + 1)}`,
  value: i + 1,
}));

const hourOptions = Array.from(Array(24).keys()).map(hour => ({
  label: `${hour % 12 || 12}:00 ${hour < 12 ? `AM` : `PM`}`,
  value: hour,
}));

export function describeSchedule(schedule: ISavedFilterSchedule) {
  const hour = hourOptions[schedule.hour]?.label ?? `${schedule.hour}:00`;

  return schedule.intervalType === SavedFilterScheduleInterval.Weekly
    ? `Weekly on ${policyWeeklyDayIntervalMap[schedule.dayIndex]} at ${hour}`
    : `Monthly on the ${schedule.dayIndex}${getOrdinalSuffix(schedule.dayIndex)} at ${hour}`;
}

// Interval / day / hour / recipients for a saved filter's scheduled email. Shared by the
// save-filter modal (on create) and the filter settings modal (on edit).
export function SavedFilterScheduleFields({ schedule, onChange, disabled }: SavedFilterScheduleFieldsProps) {
  const { data: user } = useUser();
  const orgUsers = useCurrentOrgUsers(false, [UserTypes.Inherit, UserTypes.Invited]);

  function onIntervalChange(value: string) {
    if (value === NO_EMAIL) {
      onChange(null);

      return;
    }

    onChange({
      intervalType: value as SavedFilterScheduleInterval,
      dayIndex: 1,
      hour: schedule?.hour ?? DEFAULT_HOUR,
      userIds: schedule?.userIds?.length ? schedule.userIds : (user?.id ? [user.id] : []),
    });
  }

  const dayOptions = schedule?.intervalType === SavedFilterScheduleInterval.Weekly ? weekdayOptions : monthDayOptions;
  const recipientNames = orgUsers.filter(option => schedule?.userIds.includes(option.value)).map(option => option.label);

  return (
    <>
      <Select
        label={ `Email me this view` }
        options={ intervalOptions }
        selected={ schedule?.intervalType ?? NO_EMAIL }
        onChange={ e => onIntervalChange(String(e.value)) }
        disabled={ disabled }
        description={ `A CSV of everything matching the view is emailed on the schedule.` }
      />

      { schedule && (
        <>
          <div className={ `grid grid-cols-2 gap-3` }>
            <Select
              label={ schedule.intervalType === SavedFilterScheduleInterval.Weekly ? `On` : `On the` }
              options={ dayOptions }
              selected={ schedule.dayIndex }
              onChange={ e => onChange({ ...schedule, dayIndex: Number(e.value) }) }
            />
            <Select
              label={ `At` }
              options={ hourOptions }
              selected={ schedule.hour }
              onChange={ e => onChange({ ...schedule, hour: Number(e.value) }) }
            />
          </div>
          <MultiSelect
            label={ `Send to` }
            options={ orgUsers }
            selected={ schedule.userIds }
            display={ recipientNames.length ? recipientNames.join(`, `) : `Choose users` }
            onChange={ value => onChange({ ...schedule, userIds: (value as string[]).slice(0, SAVED_FILTER_SCHEDULE_MAX_RECIPIENTS) }) }
            description={ `Up to ${SAVED_FILTER_SCHEDULE_MAX_RECIPIENTS} users of this organisation.` }
          />
        </>
      ) }
    </>
  );
}
